import React, { memo, useMemo, useState } from 'react';
import {
  Button,
  ButtonProps,
  SearchPopup,
  SearchPopupProps,
  Stack,
  Text,
} from '@adc/parallax-component-library';
import { IconPhosphorCaretDownFill } from '@adc/parallax-icons/phosphor';
import { LayoutChangeEvent } from 'react-native';
import { useAppSelector } from '../../reducers/hooks';
import { getTokenValue } from 'tamagui';
import i18n from '../../utilities/i18n';
import './SearchableDropdown.scss';

const POPUP_PADDING = getTokenValue('$2', 'space');

export type SearchableDropdownItem = {
  key: string | number;
  value: string;
};

type SearchableDropdownProps = {
  label?: string;
  items: SearchableDropdownItem[];
  selectedItem?: SearchableDropdownItem;
  onChange: (item: SearchableDropdownItem) => void;
  disabled?: boolean;
  size?: ButtonProps['size'];
  testID?: string;
};

/**
 * A dropdown component that allows users to search and select from a list of items.
 * @param {string} label - Optional label to display above the dropdown.
 * @param {SearchableDropdownItem[]} items - The list of items to display in the dropdown.
 * @param {SearchableDropdownItem} selectedItem - The currently selected item.
 * @param {(item: SearchableDropdownItem) => void} onChange - Callback function when an item is selected.
 * @param {boolean} disabled - Whether the dropdown is disabled.
 * @param {ButtonProps['size']} size - The size of the dropdown.
 * @param {string} testID - Optional test ID for testing purposes.
 * @returns {React.JSX.Element}
 */
function SearchableDropdown({
  label,
  items,
  selectedItem,
  onChange,
  disabled = false,
  size = 'large',
  testID,
}: SearchableDropdownProps) {
  const { language } = useAppSelector((state) => state.user);

  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState(selectedItem?.value || '');
  const [popupWidth, setPopupWidth] = useState<number>();
  const [offset, setOffset] = useState<SearchPopupProps['offset']>();

  const handleButtonLayout = (event: LayoutChangeEvent) => {
    if (event?.nativeEvent?.layout?.width) {
      setPopupWidth(event.nativeEvent.layout.width + POPUP_PADDING * 2);
      setOffset(-(event.nativeEvent.layout.height + POPUP_PADDING));
    }
  };

  const handleOpen = () => {
    setIsOpen(true);
  };

  const handleOpenChange = (open: boolean) => {
    if (open) {
      setSearchText('');
    } else {
      setSearchText(selectedItem?.value || '');
    }

    setIsOpen(open);
  };

  const handleSearchChange = (text: string) => {
    setSearchText(text);
  };

  const handleClearSearch = () => {
    setSearchText('');
  };

  const handleItemChange = (item: SearchableDropdownItem) => {
    onChange(item);
    setIsOpen(false);
  };

  const sortedItems = useMemo(
    () =>
      [...items].sort((a, b) =>
        a.value.localeCompare(b.value, language, {
          numeric: true,
          sensitivity: 'base',
        }),
      ),
    [items, language],
  );

  const normalizedSearchText = useMemo(
    () => searchText.trim().toLowerCase(),
    [searchText],
  );

  const filteredItems = useMemo(() => {
    const nextFilteredItems: SearchableDropdownItem[] = [];

    for (const item of sortedItems) {
      if (
        !normalizedSearchText ||
        item.value.toLowerCase().includes(normalizedSearchText)
      ) {
        nextFilteredItems.push(item);
      }
    }

    return nextFilteredItems;
  }, [sortedItems, normalizedSearchText]);

  const renderContent = () => {
    // there are no items at all, even before filtering
    if (sortedItems.length === 0) {
      return (
        <Text type={'$bodyBase.default'} padding={'$2'}>
          {i18n.t('Common.noData')}
        </Text>
      );
    }

    // there are items but none match the search filter
    if (filteredItems.length === 0) {
      return (
        <Text type={'$bodyBase.default'} padding={'$2'}>
          {i18n.t('Filters.noResults')}
        </Text>
      );
    }

    return (
      <Stack
        paddingVertical="$4"
        paddingHorizontal="$2"
        flex={1}
        className="searchable-dropdown-content"
      >
        {filteredItems.map((item) => (
          <button
            key={item.key}
            role="option"
            onClick={() => handleItemChange(item)}
            className={`searchable-dropdown-item ${item.key === selectedItem?.key ? 'selected' : ''}`}
            data-testid={`searchable-dropdown-item-${item.key}`}
          >
            <Text type={'$bodyBase.medium'}>{item.value}</Text>
          </button>
        ))}
      </Stack>
    );
  };

  return (
    <Stack
      gap="$2"
      className="searchable-dropdown"
      testID={testID || 'searchable-dropdown'}
    >
      {label ? (
        <Text
          type="$bodySmall.default"
          color="$text.60"
          textAlign="left"
          testID={`searchable-dropdown-label`}
        >
          {label}
        </Text>
      ) : null}
      <SearchPopup
        open={isOpen}
        onOpenChange={handleOpenChange}
        searchInputProps={{
          'aria-label': i18n.t('SearchableDropdown.placeholder'),
          placeholder: `${i18n.t('SearchableDropdown.placeholder')}`,
          value: searchText,
          showSearchIcon: false,
          showClearButton: true,
          onChangeText: handleSearchChange,
          onClearButtonPressed: handleClearSearch,
        }}
        content={renderContent()}
        width={popupWidth}
        offset={offset}
        testID="searchable-dropdown-popup"
      >
        <Stack onLayout={handleButtonLayout}>
          <Button
            aria-label={i18n.t('SearchableDropdown.placeholder')}
            label={selectedItem?.value || i18n.t('Common.select')}
            iconEnd={<IconPhosphorCaretDownFill />}
            flexGrow={1}
            onPress={handleOpen}
            alignStart
            variant="secondary"
            size={size}
            disabled={disabled}
            testID="searchable-dropdown-button"
            theme="Neutral"
          />
        </Stack>
      </SearchPopup>
    </Stack>
  );
}

export default memo(SearchableDropdown);
